import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Award, Sparkles, ShieldCheck, Star } from 'lucide-react';
import { Button } from '../ui/button';

const Hero = () => {
  return (
    <section className="relative overflow-x-clip overflow-y-visible safe-pt-nav pb-16 sm:pb-20 lg:pt-40 lg:pb-28 bg-gradient-to-b from-teal-50/60 via-white to-white">
      <div className="absolute top-24 -right-24 w-[420px] h-[420px] bg-teal-200/40 rounded-full blur-3xl" aria-hidden />
      <div className="absolute -bottom-20 -left-20 w-[380px] h-[380px] bg-emerald-100/50 rounded-full blur-3xl" aria-hidden />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-5 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal-100/70 border border-teal-200 text-teal-800 text-xs font-semibold mb-7">
              <Sparkles className="w-3.5 h-3.5" />
              650+ formations certifiées Qualiopi
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-slate-900 leading-[1.08] sm:leading-[1.05] tracking-tight mb-5 sm:mb-6">
              Medelia, votre <span className="bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent">partenaire santé</span>
            </h1>

            <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-7 sm:mb-8 max-w-xl">
              Formations certifiées avec prise en charge complète pour tous les professionnels de santé. Simplifiez votre formation continue en quelques clics.
            </p>

            <div className="flex flex-wrap items-center gap-3 mb-10">
              {['DPC', 'FIFPL', 'OPCO', 'CPF'].map((tag) => (
                <span key={tag} className="px-3 py-1.5 rounded-md bg-white border border-slate-200 text-xs font-semibold text-slate-700 shadow-sm">{tag}</span>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
              <Link to="/formations" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  className="w-full min-h-12 bg-teal-600 hover:bg-teal-700 text-white shadow-lg shadow-teal-600/25 px-6 touch-manipulation sm:w-auto"
                >
                  Découvrir nos formations <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/a-propos" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full min-h-12 border-slate-300 text-slate-800 hover:bg-slate-50 px-6 touch-manipulation sm:w-auto"
                >
                  En savoir plus
                </Button>
              </Link>
            </div>

            <div className="mt-10 flex items-center gap-6">
              <div className="flex -space-x-3">
                {['bg-teal-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500'].map((c, i) => (
                  <div key={i} className={`w-10 h-10 rounded-full ${c} border-2 border-white flex items-center justify-center text-white font-semibold text-xs`}>
                    {['AB', 'JK', 'SK', 'RB'][i]}
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1 text-amber-500">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
                  <span className="text-slate-900 font-bold ml-1 text-sm">4.8/5</span>
                </div>
                <p className="text-xs text-slate-500">+1450 professionnels de santé nous font confiance</p>
              </div>
            </div>
          </div>

          <div className="relative mx-auto max-w-md px-1 sm:px-0">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-teal-900/15 aspect-[4/5] w-full">
              <img
                src="https://images.pexels.com/photos/7446989/pexels-photo-7446989.jpeg?auto=compress&cs=tinysrgb&h=900"
                alt="Professionnelle de santé souriante"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />
            </div>

            <div className="absolute left-1 top-16 w-[13.5rem] rounded-2xl border border-slate-100 bg-white p-3 shadow-xl shadow-slate-900/10 sm:left-0 sm:top-20 sm:w-56 sm:p-4 lg:-left-6 lg:top-20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center">
                  <Award className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Certifié</p>
                  <p className="text-sm font-bold text-slate-900">Qualiopi</p>
                </div>
              </div>
            </div>

            <div className="absolute right-1 bottom-12 w-[14.5rem] rounded-2xl border border-slate-100 bg-white p-3 shadow-xl shadow-slate-900/10 sm:right-0 sm:bottom-16 sm:w-64 sm:p-4 lg:-right-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Prise en charge</p>
                  <p className="text-sm font-bold text-slate-900">100% financée</p>
                </div>
              </div>
              <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full w-full bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full" />
              </div>
            </div>

            <div className="absolute bottom-0 left-1/2 max-w-[calc(100%-0.5rem)] -translate-x-1/2 translate-y-1/3 rounded-2xl border border-slate-100 bg-white px-3 py-2.5 shadow-xl shadow-slate-900/10 sm:inset-x-auto sm:bottom-auto sm:left-4 sm:max-w-none sm:translate-x-0 sm:translate-y-0 sm:-bottom-3 sm:px-4 sm:py-3">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-teal-600" />
                <span className="text-xs font-semibold text-slate-900">Sécurisé & Agréé DPC</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
